<template>
    <div class="card slightly_yellow_background">
      <card-header
        :title="$t('cms.campaign.DateTimeTitle')"
        :description="$t('cms.campaign.DateTimeText')"
        :infoConfig="$CONSTANTS.infoConfig.datetime"
        :hasCheckMark="true"
        :checked="isSavedDateTime"
      >
        <template #icon>
          <icon datetime xlarge />
        </template>
      </card-header>
      <form @submit.prevent="saveDateTime" class="flex column">
        <label class="two_children_box">
          <span>{{ $t('cms.campaign.StartDateInput') }}</span>
          <input
            :value="startDate"
            @input="$emit('update:startDate', $event.target.value)"
            v-validate="'required'"
            name="startDate"
            type="date"
          />
          <div class="box_input_overlay">{{ startDateText || '-' }}</div>
        </label>

        <label class="two_children_box">
          <span>{{ $t('cms.campaign.EndDateInput') }}</span>
          <input
            :value="endDate"
            @input="$emit('update:endDate', $event.target.value)"
            v-validate="'required'"
            type="date"
            name="endDate"
          />
          <div class="box_input_overlay">{{ endDateText || '-' }}</div>
        </label>

        <button
          type="button"
          class="box"
          style="justify-content: space-between"
          @click="showDaysCheckboxes = !showDaysCheckboxes"
          :class="{
          weekday_button_uncollapsed: showDaysCheckboxes,
          is_valid: daysActive.length > 0 && !showDaysCheckboxes
        }"
        >
          {{ $t('cms.campaign.RepeatInput') }} <b>{{ campaignRepeatsText }}</b>
        </button>

        <collapse :value="showDaysCheckboxes" class="fast" id="weekday_collapse">
          <label
            :style="`font-weight: ${isEveryDay ? 800 : 300}`"
            class="day_checkbox_row"
          >
            {{ $t('cms.campaign.RepeatEveryDayChoice') }}

            <input
              type="checkbox"
              :id="campaignId + 'everyDay'"
              :checked="isEveryDay"
              @click="toggleEveryDay"
              class="weekday_checkbox"
            >
            <icon v-if="isEveryDay" tick :size="20"></icon>
          </label>

          <label
            v-for="day in this.$CONSTANTS.weekDays"
            :key="day.number"
            class="day_checkbox_row"
            :style="`font-weight: ${daysActive.includes(day.number) ? 800 : 300}`"
          >
            {{day.name}}
            <div>
              <input
                class="weekday_checkbox"
                type="checkbox"
                :id="campaignId + day.number"
                :value="day.number"
                :input="daysActive"
                @input="toggleDayActive(day.number)"
              >
              <icon v-if="!isEveryDay && daysActive.includes(day.number)" tick :size="20"/>
            </div>
          </label>
        </collapse>

        <label class="two_children_box">
          <span>{{ $t('cms.campaign.StartTimeInput') }}</span>
          <input
            :value="startTime"
            @input="$emit('update:startTime', $event.target.value)"
            type="time"
            class="flex grow"
            v-validate="'required'"
            name="startTime"
          />
          <div class="box_input_overlay">{{ startTime || '-'}}</div>
        </label>

        <label class="two_children_box">
          <span>{{ $t('cms.campaign.EndTimeInput') }}</span>
          <input
            :value="endTime"
            @input="$emit('update:endTime', $event.target.value)"
            type="time"
            v-validate="'required'"
            name="endTime"
          />
          <div class="box_input_overlay">{{ endTime || '-'}}</div>
        </label>

        <button
          type="submit"
          :disabled="!isDifferent"
          class="box primary block"
        >
          {{ $t('cms.campaign.saveButton') }}
        </button>
      </form>
    </div>
</template>

<script>
import isDateValid from 'date-fns/is_valid';
import cloneDeep from 'lodash/cloneDeep';
import CardHeader from '@/components/global/CardHeader.vue';
import { objectIsDifferent } from '@/utils/difference';

export default {
  components: {
    CardHeader,
  },
  props: {
    startDate: { type: String },
    endDate: { type: String },
    startTime: { type: String },
    endTime: { type: String },
    daysActive: { type: Array },
    campaignId: { type: String },

    startDateText: { type: String },
    endDateText: { type: String },
    campaignRepeatsText: { type: String },

    isSavedDateTime: { type: Boolean },
    isNew: { type: Boolean },
  },
  data() {
    return {
      showDaysCheckboxes: false,
      savedData: {
        startDate: this.startDate,
        endDate: this.endDate,
        startTime: this.startTime,
        endTime: this.endTime,
        daysActive: this.daysActive,
      },
      saved: false,
    };
  },
  computed: {
    isEveryDay() {
      return this.daysActive.length === 7;
    },
    isDifferent() {
      return objectIsDifferent(this.savedData, this.getCampaignDateTime());
    },
    isEndAfterStart() {
      const start = new Date(`${this.startDate}T${this.startTime}`);
      const end = new Date(`${this.endDate}T${this.endTime}`);
      if (!(isDateValid(start) && isDateValid(end))) {
        return null;
      }

      return end > start;
    },
  },
  watch: {
    isDifferent: {
      immediate: true,
      handler() {
        if (this.isDifferent) {
          this.$emit('update:isSavedDateTime', false);
        } else {
          this.startDate && this.$emit('update:isSavedDateTime', true);
        }
      },
    },
  },
  methods: {
    toggleDayActive(number) {
      let _daysActive = cloneDeep(this.daysActive);
      if (this.isEveryDay) {
        _daysActive = [number];
      } else if (this.daysActive.includes(number)) {
        _daysActive.splice(
          this.daysActive.findIndex(dayNumber => dayNumber === number), 1,
        );
      } else {
        _daysActive.push(number);
      }
      this.$emit('update:daysActive', _daysActive);
    },

    toggleEveryDay() {
      if (this.isEveryDay) {
        this.$emit('update:daysActive', []);
      } else {
        this.$emit('update:daysActive', this.$CONSTANTS.weekDays.map(weekday => weekday.number));
      }
    },
    getCampaignDateTime() {
      return {
        startDate: this.startDate,
        endDate: this.endDate,
        startTime: this.startTime,
        endTime: this.endTime,
        daysActive: this.daysActive,
      };
    },
    async saveDateTime() {
      await this.$validator.validate();
      const errors = await this.errors.all();

      if (this.daysActive.length === 0) {
        errors.push(this.$t('cms.campaign.invalidsave.CampaignRepeatsRequired'));
      }

      if (errors.length === 0 && !this.isEndAfterStart) {
        errors.push(this.$t('cms.campaign.invalidsave.StartBeforeEndRequired'));
      }

      if (errors.length > 0) {
        errors.slice(0, 3).forEach(this.showWarningNotification);
        return null;
      }
      this.savedData = this.getCampaignDateTime();
      this.saved = true;
      this.$emit('update:isSavedDateTime', false);
      !this.isNew && this.$emit('update-campaign');
      return null;
    },
  },
};

</script>
